// Templates (docs/45): what the web does with them in version 1, list them and use them (docs/47). Making and
// changing a template stays in the apps. The kit is the reference: Templates.swift (sectionTitles, TemplateWhen) and
// Store.use(_:title:areaID:when:withAttachments:), Store.isUsable, Store.sortedTemplates.
//
// Pure: the records as they are in, the records to save out.

import { makeID, noEdits, type Edits, type RuleContext } from "./edits";
import { addDays } from "./days";
import { orderAtEnd } from "./order";
import { makeProject, makeSection, makeTodo, type Area, type Project, type Section, type Template, type Todo } from "./records";
import type { Day } from "./types";

/** Two templates are free; with Pro, any number. When Pro ends, all but the first two rest. */
export const FREE_TEMPLATES = 2;

/** When a project made from a template starts: today (its first step planned for today), anytime, or a later day. */
export type TemplateWhen = { kind: "today" } | { kind: "anytime" } | { kind: "day"; day: Day };

/** The name `lastWhen` keeps. */
export function storedWhen(when: TemplateWhen): string {
  return when.kind;
}

/** What `lastWhen` says, offered again; a day is offered as tomorrow (its date isn't kept). Unknown: today. */
export function whenFromStored(name: string | undefined, today: Day): TemplateWhen {
  switch (name) {
    case "anytime": return { kind: "anytime" };
    case "day": return { kind: "day", day: addDays(today, 1) };
    default: return { kind: "today" };
  }
}

/** The sections in their order: the kept list, then any a step names that it lacks (written by an older build). */
export function sectionTitles(template: Template): string[] {
  const seen = [...(template.sections ?? [])];
  for (const step of template.steps) {
    if (step.sectionTitle !== undefined && !seen.includes(step.sectionTitle)) seen.push(step.sectionTitle);
  }
  return seen;
}

/** In the user's order. */
export function sortedTemplates(templates: readonly Template[]): Template[] {
  return [...templates].sort((a, b) => a.order - b.order);
}

/** The library's groups: by area in the areas' order, then those without an area (or whose area is gone). */
export function templateGroups(templates: readonly Template[], sortedAreas: readonly Area[]): { area?: Area; templates: Template[] }[] {
  const all = sortedTemplates(templates);
  const groups: { area?: Area; templates: Template[] }[] = [];
  for (const area of sortedAreas) {
    const inArea = all.filter((template) => template.areaID === area.id);
    if (inArea.length > 0) groups.push({ area, templates: inArea });
  }
  const loose = all.filter((template) => !sortedAreas.some((area) => area.id === template.areaID));
  if (loose.length > 0) groups.push({ templates: loose });
  return groups;
}

/** Without Pro only the first two templates, by when they were made, can be used; the others rest until Pro is back. */
export function isUsable(template: Template, templates: readonly Template[], isPro: boolean): boolean {
  if (isPro) return true;
  return [...templates].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()).slice(0, FREE_TEMPLATES).some((other) => other.id === template.id);
}

/**
 * A new project from a template (Store.use): its notes and sections, one open to-do per step (with the step's notes
 * and checklist) in `area`, numbered after everything there is. Today: the first step is planned for today, the rest
 * wait in the project; a day: the project starts then. The project remembers which to-do came from which step
 * (`templateSteps`), and the template what was chosen (`lastUsedAt`, `lastAreaID`, `lastWhen`).
 */
export function useTemplate(
  template: Template,
  input: { title: string; area: Area; when: TemplateWhen },
  data: { projects: readonly Project[]; sections: readonly Section[]; todos: readonly Todo[] },
  context: RuleContext,
): { edits: Edits; project: Project; template: Template } {
  const edits = noEdits();
  const project = makeProject({
    id: makeID(context),
    title: input.title.trim() === "" ? template.title : input.title,
    notes: template.notes,
    areaID: input.area.id,
    order: orderAtEnd(data.projects.map((other) => other.order)),
  });
  if (input.when.kind === "day") project.start = { kind: "on", day: input.when.day };
  project.templateID = template.id;

  const sectionIDs = new Map<string, string>();
  const firstSectionOrder = orderAtEnd(data.sections.map((section) => section.order));
  sectionTitles(template).forEach((title, index) => {
    const section = makeSection({ id: makeID(context), title, projectID: project.id, order: firstSectionOrder + index });
    sectionIDs.set(title, section.id);
    edits.sections.saved.push(section);
  });

  const firstOrder = orderAtEnd(data.todos.map((todo) => todo.order));
  const stepsOfTodos: Record<string, string> = {};
  template.steps.forEach((step, place) => {
    const todo = makeTodo({
      id: makeID(context), title: step.title, notes: step.notes, start: { kind: "anytime" }, projectID: project.id,
      checklist: step.checklist.map((title) => ({ id: makeID(context), title, isDone: false })),
      order: firstOrder + place, createdAt: context.now,
    });
    const sectionID = step.sectionTitle === undefined ? undefined : sectionIDs.get(step.sectionTitle);
    if (sectionID !== undefined) todo.sectionID = sectionID;
    if (input.when.kind === "today" && place === 0) todo.start = { kind: "on", day: context.today };
    stepsOfTodos[todo.id] = step.id;
    edits.todos.saved.push(todo);
  });
  project.templateSteps = stepsOfTodos;
  edits.projects.saved.unshift(project);

  const used: Template = { ...template, lastUsedAt: context.now, lastAreaID: input.area.id, lastWhen: storedWhen(input.when) };
  return { edits, project, template: used };
}
